// The approvals inbox (plan §5.1 approval_request; D-049, D-100). Counts and write-offs
// above the threshold that a station recorded without a manager's PIN are **already
// applied** (physical facts are never refused, plan §9.3): a manager confirms them, or
// rejects them, which writes compensating movements. Manager-PIN approvals given at a
// station are the history. Waiting requests first; keyboard: j / k to move, a approve,
// r reject.
//
// A later count wins (D-055): rows a count dated after them covers are not reversed by a
// rejection. The request says which, per lot (UI4A-201: a count covers rows, not products),
// the reject dialog asks the manager to acknowledge it naming the count (the held-action
// apply's pattern, D-053), and the decision shows, per lot, what it reversed and what stayed.
// The acknowledgement names the counts shown; if they changed meanwhile the server refuses
// and the dialog reads the request again. A row counted again only in a count that is
// itself waiting can't be rejected yet: the dialog links to that count's request (D-059).

import type { Approval, ReversalRow } from '@enp/domain/api';
import { approvalVisibility } from '@enp/domain/permissions';
import { writeOffValueText } from '@enp/domain/stock-control';
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  EmptyState,
  focusRing,
  FormField,
  Money,
  PageHeader,
  Skeleton,
  StatusBadge,
  Textarea,
  toast,
} from '@enp/ui';
import { localDate } from '@enp/domain/time';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  Check,
  CircleCheck,
  ClipboardCheck,
  KeyRound,
  ListChecks,
  Undo2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { meQueryOptions } from '../../auth/session.js';
import {
  PurchaseCardValue,
  PurchaseDecisionDialog,
  PurchaseRequestApproval,
} from '../../purchasing/inbox.js';
import { isTypingTarget } from '../../shell/shortcuts.js';
import { AccountDate, useAccountTimeZone } from '../../shell/account-date.js';
import { useDocumentTitle } from '../../shell/page.js';
import { ErrorPanel } from '../../shell/route-states.js';
import { Notice } from '../../stations/page-parts.js';
import { LoadMoreFooter, StockStateBadge } from '../../stock/components.js';
import {
  actionProblem,
  Kbd,
  pendingCounts,
  SignedMoney,
  useHasPermission,
} from '../../stock-control/parts.js';
import {
  approvalQuery,
  approvalsQuery,
  approvalsSearchSchema,
  decideApproval,
  invalidateStockControl,
  stockControlSettingsQuery,
  waitingApprovalsQuery,
  waitingText,
} from '../../stock-control/queries.js';
import {
  APPROVAL_MODE_LABEL,
  APPROVAL_TYPE_LABEL,
  appliedSentence,
  approvalProducts,
  approvalStatus,
  approvalValueText,
  costBasisText,
  countedSinceText,
  countsNamed,
  decidePermission,
  groundsText,
  INBOX_VIEW_LABEL,
  INBOX_VIEWS,
  type InboxView,
  levelChange,
  lineValueCents,
  nothingReversed,
  percentText,
  rejectAcknowledgement,
  rowName,
  signedText,
} from '../../stock-control/words.js';

export const Route = createFileRoute('/_app/approvals')({
  validateSearch: approvalsSearchSchema,
  component: ApprovalsPage,
});

const LIST_LABEL = 'Approval requests';

/** "Count SC-K7Q-00012", "Write-off WO-K7Q-00031". */
function subjectTitle(a: Approval): string {
  const noun =
    a.subject.type === 'stock_count'
      ? 'Count'
      : a.subject.type === 'write_off'
        ? 'Write-off'
        : APPROVAL_TYPE_LABEL[a.type];
  return a.subject.number ? `${noun} ${a.subject.number}` : noun;
}

function whoText(p: Approval['requested_by']): string {
  if (!p) return 'Someone this account doesn’t know';
  return p.name ?? (p.kind === 'admin' ? 'An admin user' : 'A staff member');
}

function whereText(a: Approval): string {
  return [a.branch?.name, a.location?.name].filter(Boolean).join(' · ') || 'Not recorded';
}

/** The biggest % change among the lines, for the list. */
function headlinePercent(a: Approval): string | null {
  const withPct = a.lines.filter((l) => l.percent !== null);
  if (withPct.length === 0) return null;
  const top = withPct.reduce((m, l) => (Number(l.percent) > Number(m.percent) ? l : m));
  return percentText(top.percent, levelChange(a.type, top.qty));
}

// ---------------------------------------------------------------- the list

function InboxItem({
  approval,
  view,
  selected,
  itemRef,
}: {
  approval: Approval;
  view: InboxView;
  selected: boolean;
  itemRef: (el: HTMLAnchorElement | null) => void;
}) {
  const pct = headlinePercent(approval);
  const status = approvalStatus(approval.status, approval.reversal?.outcome);
  const value = approvalValueText(approval);
  return (
    <li>
      <Link
        ref={itemRef}
        to="/approvals"
        search={{ ...(view === 'requested' ? {} : { view }), id: approval.id }}
        replace
        aria-current={selected ? 'true' : undefined}
        data-approval={approval.id}
        className={cn(
          'flex flex-col gap-1 border-l-2 border-transparent px-4 py-3 text-left transition-colors hover:bg-surface-hover',
          selected && 'border-primary bg-primary-subtle hover:bg-primary-subtle',
          focusRing,
        )}
      >
        <span className="flex items-baseline justify-between gap-3">
          <span className="truncate font-medium text-fg">{approvalProducts(approval)}</span>
          {approval.type === 'purchase_request' ? (
            <PurchaseCardValue approval={approval} />
          ) : value !== null ? (
            // Signed, never one unsigned sum of losses and gains (UI8B-2).
            <span className="whitespace-nowrap text-body font-semibold tabular-nums text-fg">
              {value}
            </span>
          ) : (
            <span className="whitespace-nowrap text-sm text-muted">No cost</span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
          <span>{APPROVAL_TYPE_LABEL[approval.type]}</span>
          {pct && (
            <>
              <span aria-hidden>·</span>
              <span className="tabular-nums">{pct}</span>
            </>
          )}
          <span aria-hidden>·</span>
          <AccountDate value={approval.requested_at} withTime />
        </span>
        <span className="flex flex-wrap items-center gap-2 text-sm text-muted">
          <span className="truncate">
            {whoText(approval.requested_by)} · {whereText(approval)}
          </span>
          {approval.mode === 'manager_pin' && (
            <Badge tone="neutral">
              <KeyRound aria-hidden />
              Manager PIN
            </Badge>
          )}
          {approval.status !== 'requested' && (
            <StatusBadge status={status.status}>{status.label}</StatusBadge>
          )}
        </span>
      </Link>
    </li>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col divide-y divide-border" aria-busy="true">
      <span role="status" className="sr-only">
        Loading the requests…
      </span>
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex flex-col gap-2 px-4 py-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- the decision

type Deciding = 'approve' | 'reject';

/** Some rows are reversed and some stay as counted since (D-055). */
const partly = (a: Pick<Approval, 'reversal'>) => a.reversal?.outcome === 'partly_reversed';

/**
 * One row of a rejection (UI4A-201): the lot and state, the correction signed as it moves
 * stock (UI4A-208: "+2" back on the books, "−4" off again), its value, and — when a later
 * count covers it — that it is not reversed, naming the count and its time.
 */
function ReversalRowItem({
  row,
  product,
}: {
  row: ReversalRow;
  product: Approval['lines'][number]['product'];
}) {
  const timeZone = useAccountTimeZone();
  return (
    <li className="flex items-start gap-2 px-3 py-2 text-body text-fg">
      {row.reversed ? (
        <Undo2 aria-hidden className="mt-1 size-4 shrink-0 text-muted" />
      ) : (
        <ListChecks aria-hidden className="mt-1 size-4 shrink-0 text-state-warning" />
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={cn(row.lot && 'font-mono text-sm')}>{rowName(row, product)}</span>
          <StockStateBadge state={row.state} />
          <span className="font-medium tabular-nums">{signedText(row.qty, product.base_uom)}</span>
        </span>
        <span className={cn('text-sm', row.reversed ? 'text-muted' : 'text-fg')}>
          {row.reversed
            ? 'Reversed'
            : row.counted_since
              ? `Not reversed: ${countedSinceText(row.counted_since, timeZone)}, which stands`
              : 'Not reversed'}
        </span>
      </span>
      {row.value_cents === null ? (
        <span className="whitespace-nowrap text-sm text-muted">No cost</span>
      ) : (
        <SignedMoney
          cents={row.value_cents}
          lossTone={row.reversed}
          className={cn(row.reversed ? 'font-semibold' : 'text-muted line-through')}
        />
      )}
    </li>
  );
}

/**
 * What rejecting does (waiting) or did (rejected), per product and lot (UI4A-201), and the
 * total of what is actually reversed: a row a later count covers is not in it (D-055).
 */
function ReversalBreakdown({
  approval,
  label,
  done,
}: {
  approval: Approval;
  label: string;
  /** The rejection happened (the record), rather than would happen (the dialog). */
  done: boolean;
}) {
  const summary = approval.reversal;
  const lines = approval.lines.filter((l) => (l.reversal?.rows.length ?? 0) > 0);
  if (!summary || lines.length === 0) return null;
  const costed = summary.reversed_rows - summary.reversed_uncosted_rows;
  return (
    <div className="flex flex-col gap-2">
      <ul aria-label={label} className="flex flex-col gap-2">
        {lines.map((l) => (
          <li key={l.product.id} className="flex flex-col rounded-md border border-border">
            <span className="border-b border-border px-3 py-1.5 text-sm font-medium text-fg">
              {l.product.name}
            </span>
            <ul
              aria-label={`${l.product.name}: rows`}
              className="flex flex-col divide-y divide-border"
            >
              {(l.reversal?.rows ?? []).map((row) => (
                <ReversalRowItem
                  key={`${row.lot?.id ?? ''}|${row.state}|${row.counted_since?.id ?? ''}`}
                  row={row}
                  product={l.product}
                />
              ))}
            </ul>
          </li>
        ))}
      </ul>
      <p className="text-sm text-muted" data-testid="reversal-total">
        {summary.reversed_rows === 0 ? (
          `Nothing ${done ? 'was' : 'is'} reversed: later counts cover all of it.`
        ) : (
          <>
            {costed === 0 ? (
              `No rand value: what ${done ? 'was' : 'is'} reversed has no cost`
            ) : (
              // Only what is actually reversed, net over its rows (UI4A-201).
              <>
                {done ? 'The rejection ' : 'Reversing '}
                {summary.reversed_value_cents === 0 ? (
                  `${done ? 'left' : 'leaves'} the stock value as it was`
                ) : (
                  <>
                    {summary.reversed_value_cents > 0
                      ? done
                        ? 'put back '
                        : 'puts back '
                      : done
                        ? 'took off '
                        : 'takes off '}
                    <Money
                      cents={Math.abs(summary.reversed_value_cents)}
                      className="font-semibold text-fg"
                    />{' '}
                    of stock value
                  </>
                )}{' '}
                at standard cost
                {summary.reversed_uncosted_rows > 0 &&
                  ` (and ${summary.reversed_uncosted_rows} ${summary.reversed_uncosted_rows === 1 ? 'row' : 'rows'} without a cost)`}
              </>
            )}
            {summary.not_reversed_rows > 0 &&
              `; ${summary.not_reversed_rows} ${summary.not_reversed_rows === 1 ? 'row' : 'rows'} ${done ? 'stayed' : summary.not_reversed_rows === 1 ? 'stays' : 'stay'} as counted`}
            .
          </>
        )}
      </p>
    </div>
  );
}

function DecisionDialog({
  approval: opened,
  action,
  onClose,
}: {
  approval: Approval;
  action: Deciding;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const timeZone = useAccountTimeZone();
  // The request as it is now: a count recorded since it was opened changes what rejecting
  // does (read again after a refusal).
  const current = useQuery({ ...approvalQuery(opened.id), placeholderData: opened });
  const approval = current.data ?? opened;
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [checked, setChecked] = useState(false);
  const [ackError, setAckError] = useState<string>();
  const ackRef = useRef<HTMLButtonElement>(null);
  const ack = action === 'reject' ? rejectAcknowledgement(approval, timeZone) : null;
  const none = nothingReversed(approval);
  const decide = useMutation({
    mutationFn: () =>
      decideApproval(approval.id, {
        decision: action,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        // The counts the manager was shown (CR4-23): different ones by now → 409, re-read.
        ...(ack !== null && checked
          ? { acknowledge_later_counts: [...new Set(approval.later_counts.map((r) => r.count.id))] }
          : {}),
      }),
    onSuccess: async (result) => {
      await invalidateStockControl(queryClient);
      onClose();
      if (action === 'approve') {
        toast.success(`${subjectTitle(approval)} approved`, {
          description: 'It stays in the stock ledger as recorded.',
        });
      } else {
        const n = result.compensation_movement_ids.length;
        const moves = `${n} correcting ${n === 1 ? 'movement' : 'movements'}`;
        const counts = countsNamed(result.later_counts, timeZone);
        // Neutral: a reversal may take stock off as well as put it back (UI4A-208).
        toast.success(`${subjectTitle(approval)} rejected`, {
          description:
            result.later_counts.length === 0
              ? `${moves} reversed it.`
              : n === 0
                ? `Nothing was reversed: what ${counts} found stays.`
                : `${moves} reversed the rest; what ${counts} found stays.`,
        });
      }
    },
    onError: async () => {
      // Someone may have decided it meanwhile, or a count arrived: show it as it is now.
      setChecked(false);
      await invalidateStockControl(queryClient);
    },
  });
  const failed = decide.isError ? actionProblem(decide.error, 'It wasn’t decided') : null;
  const decideFirst = decide.isError ? pendingCounts(decide.error) : [];
  const closed = failed?.title === 'Someone has decided this already';
  const noun = approval.type === 'write_off' ? 'write-off' : 'count';

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (closed) return;
    if (action === 'reject' && !reason.trim()) {
      setError('Say why, for the record.');
      return;
    }
    if (ack !== null && !checked) {
      setAckError('Tick this to reject it, or approve it instead.');
      ackRef.current?.focus();
      return;
    }
    decide.mutate();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent role="alertdialog" className="max-w-xl">
        <DialogForm onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              {closed
                ? `${subjectTitle(approval)} is already decided`
                : action === 'approve'
                  ? `Approve ${subjectTitle(approval)}?`
                  : none
                    ? `Reject ${subjectTitle(approval)}? Nothing is reversed`
                    : partly(approval)
                      ? `Reject ${subjectTitle(approval)} and reverse the rest?`
                      : `Reject ${subjectTitle(approval)} and reverse it?`}
            </DialogTitle>
            <DialogDescription>
              {closed
                ? 'Someone decided it while you were looking. A decision is final; the request now shows what was decided.'
                : action === 'approve'
                  ? `The ${noun} stays in the stock ledger as recorded. A decision is final.`
                  : none
                    ? `The ${noun} stays on record, marked rejected. Later counts cover every row, so nothing is written:`
                    : partly(approval)
                      ? `The ${noun} stays on record, marked rejected. Correcting movements are written now for the rows nobody has counted since, and the stock figures change at once:`
                      : `The ${noun} stays on record, marked rejected. These correcting movements are written now, and the stock figures change at once:`}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {action === 'reject' && !closed && (
              <ReversalBreakdown approval={approval} label="What rejecting does" done={false} />
            )}
            {!closed && ack !== null && (
              <FormField label={ack} inline required error={ackError}>
                {(control) => (
                  <Checkbox
                    {...control}
                    ref={ackRef}
                    checked={checked}
                    onCheckedChange={(v) => {
                      setChecked(v === true);
                      if (ackError) setAckError(undefined);
                    }}
                  />
                )}
              </FormField>
            )}
            {!closed && (
              <FormField
                label={action === 'approve' ? 'Note (optional)' : 'Why'}
                required={action === 'reject'}
                error={error}
                description={
                  action === 'approve'
                    ? 'For example, “Checked with the baker: the loaves went to the market.”'
                    : 'For example, “Recount found all three cakes in the cold room.”'
                }
              >
                <Textarea
                  // The one thing to do in this dialog.
                  autoFocus={action === 'reject'}
                  value={reason}
                  maxLength={500}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (error) setError(undefined);
                  }}
                />
              </FormField>
            )}
            {/* Closed: the title and description say it once (UI4A-214). */}
            {failed && !closed && (
              <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
                {failed.explain}
                {decideFirst.length > 0 && (
                  // The count's own request, to decide first (D-059, CR4-22).
                  <>
                    {' '}
                    {decideFirst.map((c, i) => (
                      <span key={c.id}>
                        {i > 0 && ', '}
                        {c.approval_request_id ? (
                          <Link
                            to="/approvals"
                            search={{ id: c.approval_request_id }}
                            onClick={onClose}
                            className="rounded-sm font-medium text-primary-text underline underline-offset-2"
                          >
                            Open count {c.number}
                          </Link>
                        ) : (
                          `Count ${c.number}`
                        )}
                      </span>
                    ))}
                  </>
                )}
              </Notice>
            )}
          </DialogBody>
          <DialogFooter>
            {closed ? (
              <Button type="button" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                {action === 'approve' ? (
                  <Button
                    type="submit"
                    autoFocus
                    icon={<Check aria-hidden />}
                    loading={decide.isPending}
                  >
                    Approve
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="destructiveSolid"
                    icon={<Undo2 aria-hidden />}
                    loading={decide.isPending}
                  >
                    {none
                      ? 'Reject'
                      : partly(approval)
                        ? 'Reject and reverse the rest'
                        : 'Reject and reverse'}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- the detail

/**
 * A waiting line's rows a later count covers, per lot (UI4A-201): rejecting leaves them as
 * counted. Nothing when no row of it is covered.
 */
function CountedSinceRows({ line }: { line: Approval['lines'][number] }) {
  const timeZone = useAccountTimeZone();
  const kept = (line.reversal?.rows ?? []).filter((r) => !r.reversed);
  if (kept.length === 0) return null;
  return (
    <ul
      aria-label={`${line.product.name}: counted since`}
      className="flex flex-col gap-1 rounded-md bg-surface-sunken px-3 py-2 text-sm text-fg"
    >
      {kept.map((row) => (
        <li
          key={`${row.lot?.id ?? ''}|${row.state}|${row.counted_since?.id ?? ''}`}
          className="flex items-start gap-2"
        >
          <ListChecks aria-hidden className="mt-0.5 size-4 shrink-0 text-state-warning" />
          <span>
            <span className={cn(row.lot && 'font-mono')}>{rowName(row, line.product)}</span>{' '}
            {row.counted_since ? countedSinceText(row.counted_since, timeZone) : 'counted since'}:
            rejecting leaves it as counted.
          </span>
        </li>
      ))}
    </ul>
  );
}

function Lines({ approval }: { approval: Approval }) {
  const settings = useQuery(stockControlSettingsQuery());
  return (
    <section aria-labelledby="approval-lines" className="flex flex-col gap-3">
      <h3 id="approval-lines" className="font-display text-base font-semibold text-fg">
        {approval.lines.length === 1
          ? 'What changed'
          : `What changed (${approval.lines.length} products)`}
      </h3>
      <ul className="flex flex-col gap-3">
        {approval.lines.map((l) => {
          const unit = l.product.base_uom;
          const pct = percentText(l.percent, levelChange(approval.type, l.qty));
          return (
            <li
              key={l.product.id}
              className="flex flex-col gap-3 rounded-md border border-border bg-surface p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <Link
                  to="/stock/$productId"
                  params={{ productId: l.product.id }}
                  className="rounded-sm font-medium text-primary-text underline-offset-2 hover:underline"
                >
                  {l.product.name}
                </Link>
                {l.value_cents !== null ? (
                  approval.type === 'write_off' ? (
                    <span className="whitespace-nowrap font-semibold tabular-nums text-fg">
                      {writeOffValueText(l.value_cents)}
                    </span>
                  ) : (
                    <SignedMoney
                      cents={lineValueCents(approval.type, l) ?? 0}
                      className="font-semibold text-fg"
                    />
                  )
                ) : (
                  <span className="text-sm text-muted">No value</span>
                )}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-body sm:grid-cols-4">
                <div className="flex flex-col">
                  <dt className="text-sm text-muted">
                    {approval.type === 'write_off' ? 'Written off' : 'Change'}
                  </dt>
                  <dd className="font-semibold tabular-nums text-fg">
                    {signedText(levelChange(approval.type, l.qty), unit)}
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-sm text-muted">Level before</dt>
                  <dd className="tabular-nums text-fg">
                    {signedText(l.level_before, unit).replace(/^\+/, '')}
                  </dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-sm text-muted">Change of level</dt>
                  <dd className="tabular-nums text-fg">{pct ?? 'Nothing before'}</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-sm text-muted">Cost basis</dt>
                  <dd className={cn(l.no_cost ? 'font-medium text-state-warning' : 'text-fg')}>
                    {costBasisText(l)}
                  </dd>
                </div>
              </dl>
              <p className="text-sm text-muted">
                Needs a manager:{' '}
                {groundsText(l, approval.type, settings.data)
                  .map((g) => g.charAt(0).toLowerCase() + g.slice(1))
                  .join('; ')}
                .
              </p>
              {approval.status === 'requested' && <CountedSinceRows line={l} />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function DecisionRecord({ approval }: { approval: Approval }) {
  const timeZone = useAccountTimeZone();
  const who =
    approval.decided_by?.name ??
    (approval.decided_by?.kind === 'staff' ? 'A manager' : 'An admin user');
  const when = approval.decided_at ? (
    <>
      {' '}
      on <AccountDate value={approval.decided_at} withTime />
    </>
  ) : null;
  const rejected = approval.status === 'rejected';
  const outcome = approval.reversal?.outcome;
  const counts = countsNamed(approval.later_counts, timeZone);
  const found = approval.later_counts.length === 1 ? 'that count' : 'those counts';
  let text: ReactNode;
  if (approval.status === 'approved' && approval.mode === 'manager_pin') {
    text = (
      <>
        Approved at the station with {who}’s PIN{when}. It never waited in this inbox.
      </>
    );
  } else if (approval.status === 'approved') {
    text = (
      <>
        Approved by {who}
        {when}. It stays in the stock ledger as recorded.
      </>
    );
  } else if (rejected) {
    // What it did, from the server's outcome (UI4A-202), never assumed.
    text = (
      <>
        Rejected by {who}
        {when}.{' '}
        {outcome === 'not_reversed'
          ? `Nothing was reversed: every row was counted again in ${counts}, and ${found} ${approval.later_counts.length === 1 ? 'stands' : 'stand'}.`
          : outcome === 'partly_reversed'
            ? `Correcting movements reversed the rows nobody had counted since; the rows counted again in ${counts} keep what ${found} found.`
            : 'Correcting movements reversed it.'}
      </>
    );
  } else {
    text = <>Closed without a decision{when}. It stays in the stock ledger as recorded.</>;
  }
  return (
    <section
      aria-labelledby="approval-decision"
      className="flex flex-col gap-2 rounded-md border border-border bg-surface-sunken p-4"
    >
      <h3 id="approval-decision" className="font-display text-base font-semibold text-fg">
        Decision
      </h3>
      <p className="text-body text-fg">{text}</p>
      {approval.reason && (
        <blockquote className="border-l-2 border-border-strong pl-3 text-body text-fg">
          “{approval.reason}”
        </blockquote>
      )}
      {rejected && <ReversalBreakdown approval={approval} label="What the rejection did" done />}
    </section>
  );
}

function RecordLink({ approval }: { approval: Approval }) {
  const timeZone = useAccountTimeZone();
  const title = subjectTitle(approval);
  if (approval.subject.type === 'stock_count') {
    return (
      <Link
        to="/stock/counts/$countId"
        params={{ countId: approval.subject.id }}
        className="rounded-sm text-primary-text underline-offset-2 hover:underline"
      >
        {title}
      </Link>
    );
  }
  if (approval.subject.type === 'write_off' && timeZone) {
    const day = localDate(new Date(approval.requested_at), timeZone);
    return (
      <Link
        to="/stock/write-offs"
        search={{ from: day, to: day, product: approval.lines[0]?.product.id }}
        className="rounded-sm text-primary-text underline-offset-2 hover:underline"
      >
        {title}
      </Link>
    );
  }
  return <span>{title}</span>;
}

function ApprovalDetail({
  approval,
  onDecide,
  canDecide,
}: {
  approval: Approval;
  onDecide: (d: Deciding) => void;
  canDecide: boolean | undefined;
}) {
  const timeZone = useAccountTimeZone();
  const status = approvalStatus(approval.status, approval.reversal?.outcome);
  const open = approval.status === 'requested';
  const permission = decidePermission(approval.type);
  return (
    <article aria-labelledby="approval-title" className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="approval-title" className="font-display text-xl font-semibold text-fg">
            {subjectTitle(approval)}
          </h2>
          <StatusBadge status={status.status}>{status.label}</StatusBadge>
        </div>
        {/* Who, when, where and the record, on one line (UI4A-2). */}
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
          <span>{APPROVAL_TYPE_LABEL[approval.type]}</span>
          <span aria-hidden>·</span>
          <span>{APPROVAL_MODE_LABEL[approval.mode]}</span>
          <span aria-hidden>·</span>
          <span>
            Recorded by {whoText(approval.requested_by)},{' '}
            <AccountDate value={approval.requested_at} withTime />
          </span>
          <span aria-hidden>·</span>
          <span>{whereText(approval)}</span>
          <span aria-hidden>·</span>
          <RecordLink approval={approval} />
        </p>
      </header>

      {open && (
        // One notice, the counted-since case included, so none contradicts another (UI4A-203).
        <Notice
          tone="warning"
          title={
            approval.later_counts.length > 0
              ? 'Already applied, and counted since'
              : 'Already applied'
          }
        >
          {appliedSentence(approval, timeZone)}
        </Notice>
      )}
      {!open && <DecisionRecord approval={approval} />}

      <Lines approval={approval} />

      {open && (
        <div className="sticky bottom-0 -mx-5 flex flex-wrap items-center justify-end gap-3 border-t border-border bg-surface-raised px-5 py-3">
          {canDecide === false ? (
            // In the actions' place, where the manager looks for them (UI4A-3).
            <Notice tone="neutral" title="You can’t decide this one" className="w-full">
              Deciding {approval.type === 'write_off' ? 'a write-off' : 'a count adjustment'} needs
              the {permission === 'writeoff.approve' ? '“approve write-offs”' : '“adjust stock”'}{' '}
              permission. Ask the owner, or a manager who has it.
            </Notice>
          ) : (
            <>
              <p className="mr-auto hidden text-sm text-muted min-[1400px]:block">
                <Kbd>a</Kbd> approve · <Kbd>r</Kbd> reject · <Kbd>j</Kbd> <Kbd>k</Kbd> next and
                previous
              </p>
              <Button
                variant="destructive"
                icon={<Undo2 aria-hidden />}
                aria-keyshortcuts="r"
                title="Reject (R)"
                onClick={() => onDecide('reject')}
              >
                Reject…
              </Button>
              <Button
                icon={<Check aria-hidden />}
                aria-keyshortcuts="a"
                title="Approve (A)"
                onClick={() => onDecide('approve')}
              >
                Approve…
              </Button>
            </>
          )}
        </div>
      )}
    </article>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <span role="status" className="sr-only">
        Loading the request…
      </span>
      <Skeleton className="h-7 w-64" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

function DetailPane({
  id,
  placeholder,
  onDecide,
}: {
  id: string;
  placeholder: Approval | undefined;
  onDecide: (a: Approval, d: Deciding) => void;
}) {
  const q = useQuery({ ...approvalQuery(id), placeholderData: placeholder });
  const approval = q.data;
  const canDecide = useHasPermission(approval ? decidePermission(approval.type) : null);
  if (q.isError && !approval) {
    return (
      <ErrorPanel
        error={q.error}
        titleAs="h2"
        what="This request"
        onRetry={() => void q.refetch()}
      />
    );
  }
  if (!approval) return <DetailSkeleton />;
  if (approval.type === 'purchase_request') {
    // Nothing is applied yet: approving makes the order (M6, D-060).
    return (
      <PurchaseRequestApproval
        approval={approval}
        canDecide={canDecide}
        onDecide={(d) => onDecide(approval, d)}
      />
    );
  }
  return (
    <ApprovalDetail
      approval={approval}
      canDecide={canDecide}
      onDecide={(d) => onDecide(approval, d)}
    />
  );
}

// ---------------------------------------------------------------- the page

function InboxViews({ current }: { current: InboxView }) {
  const waiting = useQuery(waitingApprovalsQuery()).data;
  return (
    <nav aria-label="Approval views" className="mb-4 flex flex-wrap gap-1 border-b border-border">
      {INBOX_VIEWS.map((v) => (
        <Link
          key={v}
          to="/approvals"
          search={v === 'requested' ? {} : { view: v }}
          aria-current={v === current ? 'page' : undefined}
          className={cn(
            '-mb-px inline-flex h-control items-center gap-2 border-b-2 border-transparent px-3 text-body font-medium text-muted hover:text-fg',
            v === current && 'border-primary text-fg',
            focusRing,
          )}
        >
          {INBOX_VIEW_LABEL[v]}
          {v === 'requested' && waiting && waiting.count > 0 && (
            <span className="tabular-nums">({waitingText(waiting)})</span>
          )}
        </Link>
      ))}
    </nav>
  );
}

function ApprovalsPage() {
  useDocumentTitle('Approvals');
  const search = Route.useSearch();
  const view: InboxView = search.view ?? 'requested';
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useQuery(meQueryOptions());
  // What this member sees (D-070): stock approvals with reports.view, purchase requests with
  // a purchasing permission. The server filters the list; the words follow it.
  const sees = me ? approvalVisibility(me.permissions) : { stock: true, purchase: true };
  const list = useInfiniteQuery({ ...approvalsQuery(view), placeholderData: keepPreviousData });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const itemRefs = useRef(new Map<string, HTMLAnchorElement>());
  const [deciding, setDeciding] = useState<{ approval: Approval; action: Deciding } | null>(null);

  // The open request: the one in the URL, else the first in the list.
  const selectedId = search.id ?? items[0]?.id;
  const selectedIndex = items.findIndex((a) => a.id === selectedId);
  const selected = selectedIndex >= 0 ? items[selectedIndex] : undefined;

  const select = (id: string, focus = true) => {
    void navigate({ search: (prev) => ({ ...prev, id }), replace: true });
    if (focus) itemRefs.current.get(id)?.focus();
  };

  // When the open request leaves this view (it was decided, here or by someone else), the
  // one that took its place in the list opens.
  const last = useRef<{ id: string; index: number } | null>(null);
  useEffect(() => {
    if (selectedId && selectedIndex >= 0) {
      last.current = { id: selectedId, index: selectedIndex };
      return;
    }
    const was = last.current;
    if (!list.isFetching && was && was.id === selectedId && items.length > 0) {
      const next = items[Math.min(was.index, items.length - 1)];
      if (next) select(next.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selectedId, selectedIndex, list.isFetching]);

  const canDecideSelected =
    selected !== undefined &&
    selected.status === 'requested' &&
    me !== undefined &&
    decidePermission(selected.type) !== null &&
    me.permissions.includes(decidePermission(selected.type) as never);

  // Keyboard: j / k (or the arrows in the list) move, a approves, r rejects.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (deciding || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      const inList = (e.target as HTMLElement | null)?.closest?.(`[aria-label="${LIST_LABEL}"]`);
      const down = e.key === 'j' || (inList && e.key === 'ArrowDown');
      const up = e.key === 'k' || (inList && e.key === 'ArrowUp');
      if (down || up) {
        const at = selectedIndex < 0 ? -1 : selectedIndex;
        const next = items[down ? Math.min(items.length - 1, at + 1) : Math.max(0, at - 1)];
        if (next) {
          e.preventDefault();
          select(next.id);
        }
      } else if ((e.key === 'a' || e.key === 'r') && selected && canDecideSelected) {
        e.preventDefault();
        setDeciding({ approval: selected, action: e.key === 'a' ? 'approve' : 'reject' });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const failed = list.isError && !list.data;
  const empty =
    view === 'requested' ? (
      <EmptyState
        compact
        tone="success"
        icon={CircleCheck}
        title="Nothing waiting"
        description={
          sees.stock
            ? `When a station records a count or write-off above the approval limit without a manager’s PIN, it lands here: applied straight away, you confirm it or reverse it.${sees.purchase ? ' Purchase requests wait here too.' : ''}`
            : 'When someone asks for stock, their purchase request waits here until someone who approves purchases decides it.'
        }
        action={
          <Button variant="secondary" asChild>
            <Link to="/approvals" search={{ view: 'approved' }}>
              See approved requests
              <ArrowRight aria-hidden />
            </Link>
          </Button>
        }
      />
    ) : (
      <EmptyState
        compact
        icon={ClipboardCheck}
        title={`Nothing ${INBOX_VIEW_LABEL[view].toLowerCase()} yet`}
        description={
          view === 'approved'
            ? 'Approvals given here, and manager PINs entered at a station, are listed here.'
            : view === 'rejected'
              ? 'Rejected requests are listed here, with the correcting movements they wrote.'
              : 'Requests closed without a decision are listed here.'
        }
        action={
          <Button variant="secondary" asChild>
            <Link to="/approvals">
              Back to waiting requests
              <ArrowRight aria-hidden />
            </Link>
          </Button>
        }
      />
    );

  return (
    <>
      <PageHeader
        title="Approvals"
        description={
          sees.stock
            ? `Counts and write-offs above the approval limit, recorded at a station without a manager’s PIN: already in the stock figures, your decision confirms or reverses them.${sees.purchase ? ' And purchase requests: approving makes the order.' : ''}`
            : 'Purchase requests: approving makes the order.'
        }
        actions={
          sees.stock ? (
            <Button variant="secondary" asChild>
              <Link to="/settings/stock-control">Approval limits</Link>
            </Button>
          ) : undefined
        }
      />
      <InboxViews current={view} />

      {failed ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The approvals"
          onRetry={() => void list.refetch()}
        />
      ) : !list.isPending && items.length === 0 ? (
        empty
      ) : (
        // A narrower list below xl, so the detail's facts sit above the fold on a tablet
        // (UI4A-210).
        <div className="grid items-start gap-5 lg:grid-cols-[minmax(16rem,18rem)_1fr] xl:grid-cols-[minmax(19rem,24rem)_1fr]">
          <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface-raised lg:sticky lg:top-4">
            {list.isPending ? (
              <ListSkeleton />
            ) : (
              <ol
                aria-label={LIST_LABEL}
                className="flex flex-col divide-y divide-border lg:max-h-[calc(100dvh-16rem)] lg:overflow-y-auto"
              >
                {items.map((a) => (
                  <InboxItem
                    key={a.id}
                    approval={a}
                    view={view}
                    selected={a.id === selectedId}
                    itemRef={(el) => {
                      if (el) itemRefs.current.set(a.id, el);
                      else itemRefs.current.delete(a.id);
                    }}
                  />
                ))}
              </ol>
            )}
            {!list.isPending && items.length > 0 && (
              <div className="border-t border-border px-4 py-3">
                <LoadMoreFooter
                  loaded={items.length}
                  noun={['request', 'requests']}
                  hasMore={list.hasNextPage}
                  loading={list.isFetchingNextPage}
                  updating={list.isFetching && !list.isFetchingNextPage}
                  failed={list.isError && Boolean(list.data)}
                  onMore={() => void list.fetchNextPage()}
                />
              </div>
            )}
          </div>
          <div className="min-w-0 rounded-lg border border-border bg-surface-raised p-5">
            {selectedId ? (
              <DetailPane
                key={selectedId}
                id={selectedId}
                placeholder={selected}
                onDecide={(approval, action) => setDeciding({ approval, action })}
              />
            ) : (
              <DetailSkeleton />
            )}
          </div>
        </div>
      )}
      {deciding &&
        (deciding.approval.type === 'purchase_request' ? (
          <PurchaseDecisionDialog
            approval={deciding.approval}
            action={deciding.action}
            onClose={() => setDeciding(null)}
          />
        ) : (
          <DecisionDialog
            approval={deciding.approval}
            action={deciding.action}
            onClose={() => setDeciding(null)}
          />
        ))}
    </>
  );
}
